import React, { useEffect, useMemo, useState } from "react"
import { Trash2 } from "lucide-react"
import useApi from "../../hooks/useApi.js"

const RULE_TYPES = [ "url_match", "css_selector", "custom_event" ]

const THANK_YOU_RE = /thank|order|success|confirm|complete|tak|merci|danke|gracias/i

export default function DefineConversionStep({ t, profile, onContinued }) {
  const api = useApi()
  const [ rules, setRules ] = useState([])
  const [ loadingRules, setLoadingRules ] = useState(true)
  const [ ruleType, setRuleType ] = useState("url_match")
  const [ pattern, setPattern ] = useState("")
  const [ adding, setAdding ] = useState(false)
  const [ addError, setAddError ] = useState(null)
  const [ removingId, setRemovingId ] = useState(null)
  const [ suggestionsBusy, setSuggestionsBusy ] = useState(null)
  const [ submitting, setSubmitting ] = useState(null)
  const [ error, setError ] = useState(null)
  const [ pages, setPages ] = useState([])

  useEffect(() => {
    let cancelled = false
    Promise.all([
      api.get("/api/v1/shop/conversion_rules"),
      api.get("/api/v1/shop/pages")
    ])
      .then(([ r, p ]) => {
        if (cancelled) return
        setRules(r || [])
        setPages(p || [])
        setLoadingRules(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err.message)
        setLoadingRules(false)
      })
    return () => { cancelled = true }
  }, [ api ])

  const suggestions = useMemo(() => {
    const existingPatterns = new Set(rules.map((r) => r.pattern))
    return pages
      .filter((p) => THANK_YOU_RE.test(p.path))
      .filter((p) => !existingPatterns.has(`${p.path}*`) && !existingPatterns.has(p.path))
      .slice(0, 5)
  }, [ pages, rules ])

  const placeholderByType = {
    url_match: t("dashboard.onboarding.define_conversion.pattern_placeholder_url_match"),
    css_selector: t("dashboard.onboarding.define_conversion.pattern_placeholder_css_selector"),
    custom_event: t("dashboard.onboarding.define_conversion.pattern_placeholder_custom_event")
  }

  const labelByType = {
    url_match: t("dashboard.onboarding.define_conversion.pattern_label_url_match"),
    css_selector: t("dashboard.onboarding.define_conversion.pattern_label_css_selector"),
    custom_event: t("dashboard.onboarding.define_conversion.pattern_label_custom_event")
  }

  const addRule = async (rule_type, raw_pattern) => {
    const value = raw_pattern.trim()
    if (!value) return
    setAdding(true)
    setAddError(null)
    try {
      const created = await api.post("/api/v1/shop/conversion_rules", {
        conversion_rule: { rule_type, pattern: value }
      })
      setRules((prev) => [ ...prev, created ])
      setPattern("")
    } catch (err) {
      setAddError(err.message)
    } finally {
      setAdding(false)
    }
  }

  const submitForm = (e) => {
    e.preventDefault()
    addRule(ruleType, pattern)
  }

  const removeRule = async (rule) => {
    setRemovingId(rule.id)
    try {
      await api.destroy(`/api/v1/shop/conversion_rules/${rule.id}`)
      setRules((prev) => prev.filter((r) => r.id !== rule.id))
    } catch (err) {
      setError(err.message)
    } finally {
      setRemovingId(null)
    }
  }

  const addSuggestion = async (path) => {
    setSuggestionsBusy(path)
    try {
      const created = await api.post("/api/v1/shop/conversion_rules", {
        conversion_rule: { rule_type: "url_match", pattern: `${path}*` }
      })
      setRules((prev) => [ ...prev, created ])
    } catch (err) {
      setError(err.message)
    } finally {
      setSuggestionsBusy(null)
    }
  }

  const canContinue = rules.length > 0

  const handleContinue = async () => {
    setSubmitting("continue")
    setError(null)
    try {
      await api.post("/api/v1/shop/analyze")
      onContinued()
    } catch (err) {
      setError(err.message)
      setSubmitting(null)
    }
  }

  const handleSkip = async () => {
    setSubmitting("skip")
    setError(null)
    try {
      await api.patch("/api/v1/shop/setting", {
        setting: { conversion_setup_skipped_at: new Date().toISOString() }
      })
      await api.post("/api/v1/shop/analyze")
      onContinued()
    } catch (err) {
      setError(err.message)
      setSubmitting(null)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[28px] font-medium tracking-[-0.035em] text-ink-primary">
          {t("dashboard.onboarding.define_conversion.title")}
        </h1>
        <p className="mt-2 text-sm text-ink-secondary leading-relaxed">
          {t("dashboard.onboarding.define_conversion.lede")}
        </p>
        <p className="mt-3 text-[13px] text-negative" style={{ lineHeight: 1.55 }}>
          {t("dashboard.onboarding.define_conversion.stakes")}
        </p>
      </div>

      <form
        onSubmit={submitForm}
        className="border-t border-rule pt-6 space-y-3"
      >
        <div className="flex flex-wrap gap-3 items-end">
          <label className="block">
            <span className="block font-mono text-[10px] tracking-[0.12em] text-ink-tertiary">
              {t("dashboard.onboarding.define_conversion.rule_type_label")}
            </span>
            <select
              value={ruleType}
              onChange={(e) => setRuleType(e.target.value)}
              className="mt-1 bg-surface-card border border-rule rounded-button px-3 py-2 text-[13px] text-ink-primary focus:outline-none focus:border-brand"
            >
              {RULE_TYPES.map((rt) => (
                <option key={rt} value={rt}>
                  {t(`dashboard.onboarding.define_conversion.rule_type_${rt}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="block flex-1 min-w-[200px]">
            <span className="block font-mono text-[10px] tracking-[0.12em] text-ink-tertiary">
              {labelByType[ruleType]}
            </span>
            <input
              type="text"
              required
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder={placeholderByType[ruleType]}
              className="mt-1 w-full bg-surface-card border border-rule rounded-button px-3 py-2 text-sm font-mono text-[12px] text-ink-primary placeholder:text-ink-faint focus:outline-none focus:border-brand"
            />
          </label>
          <button
            type="submit"
            disabled={adding || !pattern.trim()}
            className="bg-surface-card border border-rule rounded-button px-4 py-2 text-[13px] font-medium text-ink-primary disabled:opacity-60 h-[38px]"
          >
            {adding
              ? t("dashboard.onboarding.define_conversion.adding")
              : t("dashboard.onboarding.define_conversion.add")}
          </button>
        </div>
        {addError && <p className="text-sm text-failure">{addError}</p>}
      </form>

      {!loadingRules && suggestions.length > 0 && (
        <section className="space-y-2 border-t border-rule pt-6">
          <p
            className="font-mono text-[10px] tracking-[0.15em] text-ink-tertiary"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {t("dashboard.onboarding.define_conversion.suggestions_heading")}
          </p>
          <ul className="space-y-1">
            {suggestions.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-4 py-2 border-b border-rule"
              >
                <span className="font-mono text-[12px] text-ink-secondary truncate">{p.path}*</span>
                <button
                  type="button"
                  onClick={() => addSuggestion(p.path)}
                  disabled={suggestionsBusy === p.path}
                  className="text-ai text-[13px] font-medium disabled:opacity-60 whitespace-nowrap"
                >
                  {suggestionsBusy === p.path
                    ? t("dashboard.onboarding.define_conversion.adding")
                    : t("dashboard.onboarding.define_conversion.add")}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-2 border-t border-rule pt-6">
        <p
          className="font-mono text-[10px] tracking-[0.15em] text-ink-tertiary"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {t("dashboard.onboarding.define_conversion.rules_heading")}
        </p>
        {loadingRules ? (
          <p className="text-sm text-ink-tertiary">…</p>
        ) : rules.length === 0 ? (
          <p className="text-[13px] text-ink-tertiary" style={{ lineHeight: 1.55 }}>
            {t("dashboard.onboarding.define_conversion.rules_empty")}
          </p>
        ) : (
          <ul className="space-y-2">
            {rules.map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between gap-3 bg-surface-card border border-rule rounded-card px-4 py-3"
              >
                <div className="min-w-0">
                  <span
                    className="font-mono text-[10px] text-ai"
                    style={{ letterSpacing: "0.10em" }}
                  >
                    {t(`dashboard.onboarding.define_conversion.rule_type_${r.rule_type}`)}
                  </span>
                  <code
                    className="block font-mono text-[12px] text-ink-primary mt-1 truncate"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {r.pattern}
                  </code>
                </div>
                <button
                  type="button"
                  onClick={() => removeRule(r)}
                  disabled={removingId === r.id}
                  aria-label={t("dashboard.onboarding.define_conversion.remove")}
                  className="text-ink-faint hover:text-failure transition-colors disabled:opacity-40"
                >
                  <Trash2 size={14} strokeWidth={1.75} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {error && <p className="text-sm text-failure">{error}</p>}

      <div className="flex items-center gap-4 pt-2 border-t border-rule">
        <button
          type="button"
          onClick={handleContinue}
          disabled={!canContinue || submitting != null}
          className="inline-block bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting === "continue"
            ? t("dashboard.onboarding.define_conversion.submitting")
            : t("dashboard.onboarding.define_conversion.button_continue")}
        </button>
        <button
          type="button"
          onClick={handleSkip}
          disabled={submitting != null}
          className="text-[13px] font-medium text-ai hover:underline disabled:opacity-60"
        >
          {submitting === "skip"
            ? t("dashboard.onboarding.define_conversion.skipping")
            : t("dashboard.onboarding.define_conversion.button_skip")}
        </button>
        {!canContinue && submitting == null && (
          <p className="text-[12px] text-ink-tertiary">
            {t("dashboard.onboarding.define_conversion.button_continue_requires")}
          </p>
        )}
      </div>
    </div>
  )
}
